import { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import type { Post } from '@/lib/api';

/**
 * AI-002: "Why This?" feed explainability — shows a brief reason
 * why an article appears in the user's feed. Derived from signals
 * already on the Post object (trending, source reputation, tags,
 * engagement, recency). Shown as a subtle pill that expands on tap.
 */

function buildReason(post: Post): string | null {
  const reasons: string[] = [];

  // Trending signal
  if (post.isTrending) {
    reasons.push('Trending in the community');
  }

  // High engagement
  if ((post.upvoteCount ?? 0) >= 10) {
    reasons.push(`${post.upvoteCount} upvotes from enthusiasts`);
  }

  // Source reputation
  if (post.source?.reputationScore && post.source.reputationScore >= 80) {
    reasons.push(`Trusted source: ${post.source.name}`);
  } else if (post.source?.name) {
    reasons.push(`From ${post.source.name}`);
  }

  // Recency
  const ageMs = Date.now() - new Date(post.publishedAt).getTime();
  const THREE_HOURS = 3 * 60 * 60 * 1000;
  if (ageMs < THREE_HOURS) {
    reasons.push('Published recently');
  }

  // AI relevance
  if (post.aiRelevanceScore && post.aiRelevanceScore >= 0.8) {
    reasons.push('High relevance score');
  }

  // Brand/category match
  if (post.brands && post.brands.length > 0) {
    reasons.push(`Covers ${post.brands.slice(0, 2).join(' & ')}`);
  }

  // Garage activity
  if ((post.garageActivityCount ?? 0) >= 5) {
    reasons.push('Active in Garage discussions');
  }

  // Community reactions
  if (post.topReaction && (post.reactionCount ?? 0) >= 3) {
    const emoji = post.topReaction === 'fire' ? '🔥' : post.topReaction === 'interesting' ? '💡' : '❤️';
    reasons.push(`${emoji} ${post.reactionCount} reactions`);
  }

  if (reasons.length === 0) return null;
  // Show top 2 reasons to keep it concise
  return reasons.slice(0, 2).join(' · ');
}

export function WhyThisCard({ post }: { post: Post }) {
  const [expanded, setExpanded] = useState(false);
  const reason = buildReason(post);

  if (!reason) return null;

  return (
    <div className="relative z-10 mt-1.5">
      {!expanded ? (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(true);
          }}
          className="inline-flex items-center gap-1 rounded-full bg-primary-500/10 px-2 py-0.5 text-[10px] font-medium text-primary-400 ring-1 ring-primary-500/20 hover:bg-primary-500/15 hover:ring-primary-500/30 transition-all duration-200 cursor-pointer"
        >
          <Sparkles className="h-2.5 w-2.5" />
          Why this?
        </button>
      ) : (
        <div className="inline-flex items-center gap-1.5 rounded-md bg-primary-500/10 px-2.5 py-1 text-[11px] text-primary-300 ring-1 ring-primary-500/20 animate-in fade-in duration-200">
          <Sparkles className="h-3 w-3 shrink-0 text-primary-400" />
          <span>{reason}</span>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setExpanded(false);
            }}
            className="ml-1 shrink-0 rounded-full p-0.5 hover:bg-primary-500/20 transition-colors cursor-pointer"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      )}
    </div>
  );
}
